import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FolderKanban,
  AlertTriangle,
  TrendingUp,
  Clock,
  Building2,
  ChevronDown,
  GripVertical,
  Eye,
  EyeOff,
  Settings2,
  Plus,
  X,
  Pin,
  Star,
  ShieldAlert,
  History,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useProjectStore } from '../store/useProjectStore';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';
import { useOrgHierarchyStore } from '../store/useOrgHierarchyStore';
import { useDashboardConfigStore } from '../store/useDashboardConfigStore';
import { useAuthStore } from '../store/useAuthStore';
import { useWorkItemStore } from '../store/useWorkItemStore';
import RefreshButton from '../components/common/RefreshButton';
import type { DashboardTile } from '../types';

const DONE_STATUSES = new Set(['done', 'completed', 'closed']);

export default function DashboardPage() {
  const { projects, loading, fetchProjects } = useProjectStore();
  const { setSelectedProject } = useAppStore();
  const { getHealthColor } = useConfigStore();
  const { organizations, fetchOrganizations, selectedOrgId, setSelectedOrg } = useOrgHierarchyStore();
  const { config: dashConfig, fetchConfig: fetchDashConfig, updateTiles, updateQuickAccess, setSelectedOrg: setDashOrg } = useDashboardConfigStore();
  const { user } = useAuthStore();
  const { items: workItems, fetchByProjects } = useWorkItemStore();
  const navigate = useNavigate();
  const [modifyMode, setModifyMode] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [showProjectPicker, setShowProjectPicker] = useState(false);

  const MAX_QUICK_ACCESS = 8;
  // Deliberately small: a long recency list stops being recent and
  // becomes a second, worse project list.
  const MAX_RECENT = 6;

  useEffect(() => {
    fetchOrganizations();
    if (user) fetchDashConfig();
  }, [fetchOrganizations, fetchDashConfig, user]);

  // Pull work items for the current project set so the security tile has data.
  // Cheap optimisation: only fetch projects that haven't been loaded yet.
  useEffect(() => {
    if (projects.length === 0) return;
    const loadedProjectIds = new Set(workItems.map((i) => i.projectId));
    const missing = projects.map((p) => p._id).filter((id) => !loadedProjectIds.has(id));
    if (missing.length > 0) {
      fetchByProjects(missing).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length]);

  // Quick access project IDs from config
  const quickAccessIds: string[] = dashConfig?.quickAccessProjectIds || [];

  // Quick access projects (resolved from IDs, preserving order)
  const quickAccessProjects = useMemo(() => {
    return quickAccessIds
      .map((id) => projects.find((p) => p._id === id))
      .filter(Boolean) as typeof projects;
  }, [quickAccessIds, projects]);

  // Recently opened, most recent first. Automatic and unconfigured, in contrast
  // to Quick Access below it. Recency rather than frequency on purpose: a user
  // can always explain why something is in a recency list, whereas a frequency
  // ranking goes stale and reads as simply wrong.
  const recentProjects = useMemo(() => {
    const ids: string[] = dashConfig?.recentProjectIds || [];
    return ids
      .map((id) => projects.find((p) => p._id === id))
      // A project the user has lost access to must not linger as a dead link.
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      // No point showing something that is already pinned right below.
      .filter((p) => !quickAccessIds.includes(p._id))
      .slice(0, MAX_RECENT);
  }, [dashConfig?.recentProjectIds, projects, quickAccessIds]);

  // Projects NOT in quick access (available for adding)
  const availableProjects = useMemo(() => {
    const qaSet = new Set(quickAccessIds);
    return projects.filter((p) => !qaSet.has(p._id));
  }, [quickAccessIds, projects]);

  // Filter projects by selected org
  const filteredProjects = selectedOrgId
    ? projects.filter((p) => p.organizationId === selectedOrgId)
    : projects;

  const handleProjectClick = (projectId: string) => {
    setSelectedProject(projectId);
    navigate(`/project/${projectId}`);
  };

  const handleOrgSelect = (orgId: string | null) => {
    setSelectedOrg(orgId);
    if (orgId) setDashOrg(orgId);
    setOrgDropdownOpen(false);
  };

  const addToQuickAccess = (projectId: string) => {
    if (quickAccessIds.length >= MAX_QUICK_ACCESS) return;
    const updated = [...quickAccessIds, projectId];
    updateQuickAccess(updated);
  };

  const removeFromQuickAccess = (projectId: string) => {
    const updated = quickAccessIds.filter((id) => id !== projectId);
    updateQuickAccess(updated);
  };

  // Tile defaults
  const defaultTiles: DashboardTile[] = [
    { id: 'healthSummary', label: 'Health Summary', visible: true, order: 0, size: 'small' },
    { id: 'overdueItems', label: 'Overdue Items', visible: true, order: 1, size: 'small' },
    { id: 'recentActivity', label: 'Recent Activity', visible: true, order: 2, size: 'medium' },
    { id: 'securityOverview', label: 'Security Overview', visible: true, order: 3, size: 'medium' },
  ];

  const tiles = dashConfig?.tiles?.length ? dashConfig.tiles : defaultTiles;
  const visibleTiles = [...tiles].filter((t) => t.visible).sort((a, b) => a.order - b.order);

  const toggleTileVisibility = (tileId: string) => {
    const updated = tiles.map((t) =>
      t.id === tileId ? { ...t, visible: !t.visible } : t
    );
    updateTiles(updated);
  };

  // Stats
  const healthCounts = filteredProjects.reduce(
    (acc, p) => {
      acc[p.health] = (acc[p.health] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
  const healthData = Object.entries(healthCounts).map(([key, value]) => ({
    name: key,
    value,
    color: getHealthColor(key),
  }));

  const overdueProjects = filteredProjects.filter((p) => p.stats && p.stats.overdue > 0);
  const totalTasks = filteredProjects.reduce((sum, p) => sum + (p.stats?.total || 0), 0);
  const completedTasks = filteredProjects.reduce((sum, p) => sum + (p.stats?.completed || 0), 0);

  const selectedOrg = organizations.find((o) => o._id === selectedOrgId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  // Tile renderers
  const renderTile = (tile: DashboardTile) => {
    switch (tile.id) {
      case 'healthSummary':
        return renderHealthSummary();
      case 'overdueItems':
        return renderOverdueItems();
      case 'recentActivity':
        return renderRecentActivity();
      case 'securityOverview':
        return renderSecurityOverview();
      default:
        return null;
    }
  };

  const renderHealthSummary = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Project Health</h3>
      {healthData.length > 0 ? (
        <>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={healthData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  dataKey="value"
                  stroke="none"
                >
                  {healthData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [String(value), String(name).replace('-', ' ')]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {healthData.map((h) => (
              <div key={h.name} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: h.color }} />
                <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{h.name.replace('-', ' ')}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">No health data</p>
      )}
    </div>
  );

  const renderOverdueItems = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Overdue Items</h3>
      {overdueProjects.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No overdue items</p>
      ) : (
        <div className="space-y-2">
          {overdueProjects.map((p) => (
            <button
              key={p._id}
              onClick={() => handleProjectClick(p._id)}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
            >
              <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{p.name}</span>
              <span className="text-xs font-medium text-red-500">{p.stats?.overdue} overdue</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderRecentActivity = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Recent Activity</h3>
      <p className="text-sm text-gray-400 dark:text-gray-500">Activity feed coming soon</p>
    </div>
  );

  const renderSecurityOverview = () => {
    const SEV_COLORS: Record<string, string> = {
      info: '#94a3b8', low: '#06b6d4', medium: '#f59e0b', high: '#f97316', critical: '#ef4444',
    };
    const securityItems = workItems.filter(
      (i) => i.workItemType === 'security' && !DONE_STATUSES.has(i.status),
    );
    const sevCounts: Record<string, number> = { info: 0, low: 0, medium: 0, high: 0, critical: 0 };
    for (const it of securityItems) {
      const s = (it.customFields?.severity as string) || 'medium';
      sevCounts[s] = (sevCounts[s] || 0) + 1;
    }
    const now = Date.now();
    const ageBuckets = { '<7d': 0, '7-30d': 0, '30-90d': 0, '>90d': 0 };
    for (const it of securityItems) {
      const created = it.createdAt ? new Date(it.createdAt).getTime() : now;
      const days = (now - created) / (1000 * 60 * 60 * 24);
      if (days < 7) ageBuckets['<7d']++;
      else if (days < 30) ageBuckets['7-30d']++;
      else if (days < 90) ageBuckets['30-90d']++;
      else ageBuckets['>90d']++;
    }
    const total = securityItems.length;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ShieldAlert size={14} className="text-orange-500" />
            Security Overview
          </h3>
          <span className="text-xs text-gray-400 dark:text-gray-500">{total} open</span>
        </div>
        {total === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">No open security items</p>
        ) : (
          <>
            <div className="grid grid-cols-5 gap-1.5 mb-3">
              {(['info', 'low', 'medium', 'high', 'critical'] as const).map((s) => (
                <div key={s} className="text-center p-1.5 rounded" style={{ backgroundColor: SEV_COLORS[s] + '15' }}>
                  <div className="text-lg font-bold" style={{ color: SEV_COLORS[s] }}>{sevCounts[s] || 0}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{s}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
              {(Object.keys(ageBuckets) as Array<keyof typeof ageBuckets>).map((b) => (
                <div key={b} className="text-center">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{ageBuckets[b]}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{b}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with org dropdown */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} &middot;{' '}
            {totalTasks} total work items
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Org filter dropdown */}
          <div className="relative">
            <button
              onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm hover:border-accent/50 transition-colors"
            >
              <Building2 size={14} className="text-gray-400" />
              <span className="text-gray-700 dark:text-gray-300">
                {selectedOrg ? selectedOrg.name : 'All Organizations'}
              </span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {orgDropdownOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                <button
                  onClick={() => handleOrgSelect(null)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg"
                >
                  All Organizations
                </button>
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => handleOrgSelect(org._id)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {org.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <RefreshButton
            onRefresh={async () => {
              await Promise.all([
                fetchProjects(),
                fetchOrganizations(),
                fetchDashConfig(),
              ]);
            }}
            tooltip="Refresh dashboard"
          />

          {/* Customize mode toggle */}
          <button
            onClick={() => setModifyMode(!modifyMode)}
            className={`p-2 rounded-lg transition-colors ${
              modifyMode
                ? 'bg-accent/20 text-accent'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
            title={modifyMode ? 'Exit customize mode' : 'Customize dashboard'}
          >
            <Settings2 size={16} />
          </button>
        </div>
      </div>

      {/* Tile config panel - only in customize mode */}
      {modifyMode && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Dashboard Tiles</h3>
          <div className="space-y-2">
            {tiles.map((tile) => (
              <div key={tile.id} className="flex items-center gap-3 px-2 py-1.5">
                <GripVertical size={14} className="text-gray-300" />
                <button
                  onClick={() => toggleTileVisibility(tile.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {tile.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <span className={`text-sm ${tile.visible ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 line-through'}`}>
                  {tile.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent — automatic. Sits above Quick Access because it needs no
          setup, so a new user sees something useful on day one. */}
      {recentProjects.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <History size={18} className="text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Recent
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              where you were last working
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {recentProjects.map((project) => (
              <div
                key={project._id}
                className="group flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-accent/50 transition-colors"
              >
                <button
                  onClick={() => navigate(`/project/${project._id}`)}
                  className="flex items-center gap-2 min-w-0 text-left"
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: getHealthColor(project.health) }}
                  />
                  <span className="min-w-0">
                    {project.displayId && (
                      <span className="font-mono text-[10px] text-gray-400 mr-1.5">
                        {project.displayId}
                      </span>
                    )}
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                      {project.name}
                    </span>
                  </span>
                </button>
                {quickAccessIds.length < MAX_QUICK_ACCESS && (
                  <button
                    onClick={() => updateQuickAccess([...quickAccessIds, project._id])}
                    title="Pin to Quick Access"
                    aria-label={`Pin ${project.name} to Quick Access`}
                    className="p-1 rounded text-gray-300 dark:text-gray-600 hover:text-amber-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    <Star size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Access Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star size={18} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Quick Access
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              ({quickAccessProjects.length}/{MAX_QUICK_ACCESS})
            </span>
          </div>
          {modifyMode && quickAccessIds.length < MAX_QUICK_ACCESS && (
            <button
              onClick={() => setShowProjectPicker(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-accent hover:bg-accent/10 rounded-lg transition-colors"
            >
              <Plus size={14} />
              Add Project
            </button>
          )}
        </div>

        {quickAccessProjects.length === 0 ? (
          modifyMode ? (
            <button
              onClick={() => setShowProjectPicker(true)}
              className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center gap-2 hover:border-accent/50 hover:bg-accent/5 transition-colors group"
            >
              <Plus size={24} className="text-gray-400 dark:text-gray-500 group-hover:text-accent transition-colors" />
              <span className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-accent transition-colors">
                Pin up to {MAX_QUICK_ACCESS} projects for quick access
              </span>
            </button>
          ) : (
            <div className="text-sm text-gray-400 dark:text-gray-500 py-4">
              No pinned projects. Click the <Settings2 size={14} className="inline-block mx-0.5 -mt-0.5" /> icon to customize.
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickAccessProjects.map((project) => (
              <div
                key={project._id}
                className="relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-accent/50 hover:shadow-md transition-all group"
              >
                {/* Remove button - only in customize mode */}
                {modifyMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromQuickAccess(project._id); }}
                    className="absolute top-2 right-2 p-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 opacity-0 group-hover:opacity-100 transition-all z-10"
                    title="Remove from Quick Access"
                  >
                    <X size={12} />
                  </button>
                )}

                {/* Clickable area */}
                <button
                  onClick={() => handleProjectClick(project._id)}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: getHealthColor(project.health) }}
                    />
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate group-hover:text-accent transition-colors">
                      {project.displayId && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{project.displayId}</span>}
                      {project.name}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mb-2">{project.status}</p>
                  {project.stats && project.stats.total > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                        <span>{project.stats.completed}/{project.stats.total}</span>
                        <span>{project.stats.completion}%</span>
                      </div>
                      <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent rounded-full"
                          style={{ width: `${project.stats.completion}%` }}
                        />
                      </div>
                    </div>
                  )}
                </button>
              </div>
            ))}

            {/* Empty slots with add button - only in customize mode */}
            {modifyMode && quickAccessIds.length < MAX_QUICK_ACCESS && (
              <button
                onClick={() => setShowProjectPicker(true)}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 flex flex-col items-center justify-center gap-1 hover:border-accent/50 hover:bg-accent/5 transition-colors group min-h-25"
              >
                <Plus size={18} className="text-gray-400 group-hover:text-accent transition-colors" />
                <span className="text-xs text-gray-400 group-hover:text-accent transition-colors">Add</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
              <FolderKanban size={20} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{filteredProjects.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Projects</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <TrendingUp size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{completedTasks}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Completed Items</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Clock size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalTasks - completedTasks}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">In Progress</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {filteredProjects.reduce((sum, p) => sum + (p.stats?.overdue || 0), 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Overdue Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Tile Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {visibleTiles.map((tile) => (
          <div
            key={tile.id}
            className={
              tile.size === 'large' ? 'lg:col-span-2' :
              tile.size === 'medium' ? 'lg:col-span-2' :
              ''
            }
          >
            {renderTile(tile)}
          </div>
        ))}
      </div>

      {/* Project Picker Modal */}
      {showProjectPicker && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowProjectPicker(false)}
          />

          {/* Panel */}
          <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
            <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 pointer-events-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Add to Quick Access
                </h3>
                <button
                  onClick={() => setShowProjectPicker(false)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={18} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Select projects to pin to your dashboard ({quickAccessIds.length}/{MAX_QUICK_ACCESS} used)
              </p>

              {availableProjects.length === 0 ? (
                <div className="py-8 text-center text-gray-400 dark:text-gray-500">
                  <FolderKanban size={32} className="mx-auto mb-2" />
                  <p className="text-sm">All projects are already pinned</p>
                </div>
              ) : (
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {availableProjects.map((project) => (
                    <button
                      key={project._id}
                      onClick={() => {
                        addToQuickAccess(project._id);
                        if (quickAccessIds.length + 1 >= MAX_QUICK_ACCESS) {
                          setShowProjectPicker(false);
                        }
                      }}
                      disabled={quickAccessIds.length >= MAX_QUICK_ACCESS}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: getHealthColor(project.health) }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {project.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{project.status}</p>
                      </div>
                      <Pin size={14} className="text-gray-400 shrink-0 pointer-events-none" />
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowProjectPicker(false)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
